import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Iframe from 'react-iframe';
import debug from 'debug';
import { payixUrl } from '../../config';
import { extractOrigin, buildIframeUrl, parsePayixResponse, PAYIX_RESPONSE_KINDS } from './payixHelpers';

const debugPayix = debug('payix:iframe');

function reportPayixError(error, context) {
  if (typeof window !== 'undefined' && window.DD_RUM) {
    window.DD_RUM.addError(error, { source: 'payix_tokenization', ...context });
  }
}

/**
 * Payix Iframe Component for Payment Method Tokenization
 *
 * Creates secure payment method tokens (cards/ACH) via Payix PCI-compliant iframe.
 * Handles postMessage communication and origin validation.
 *
 * @component
 * @example
 * <PayixIframe
 *   accountNumber="4010"
 *   paymentType="card"
 *   onToken={(tokenData) => console.log('Token:', tokenData)}
 *   onError={(error) => console.error('Error:', error)}
 * />
 */
export default function PayixIframe({
  accountNumber,
  onToken = undefined,
  onError = undefined,
  onMessage = undefined,
  paymentType = 'card',
  height = 450,
  className = undefined,
  style = undefined,
  showLoader = true,
  zoom = 1,
}) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Extract origin for security validation
  const payixOrigin = useMemo(() => extractOrigin(payixUrl), []);

  // Construct iframe URL — pure, no side effects
  const { iframeUrl, urlError } = useMemo(() => {
    try {
      return { iframeUrl: buildIframeUrl({ baseUrl: payixUrl, accountNumber, paymentType }), urlError: null };
    } catch (err) {
      debugPayix('Error constructing URL: %O', err);
      reportPayixError(err, { reason: 'url_construction_failed', paymentType });
      return { iframeUrl: null, urlError: 'Failed to construct Payix URL' };
    }
  }, [accountNumber, paymentType]);

  // Only URL construction failure is fatal and unmounts the iframe.
  // Tokenization failures (PAYIX_RESPONSE_KINDS.FAILURE) are surfaced via onError to the parent
  // so the user can correct their input and retry without losing the iframe.
  const error = urlError;

  // Reset load state when the iframe URL changes so the loading overlay re-appears.
  useEffect(() => {
    setIsLoaded(false);
  }, [iframeUrl]);

  // Handle postMessage events from Payix iframe
  useEffect(() => {
    if (!iframeUrl) return undefined;

    const handleMessage = (event) => {
      // Security: Validate origin
      if (payixOrigin && event.origin !== payixOrigin) {
        debugPayix('Ignoring message from untrusted origin: %s', event.origin);
        return;
      }

      // Call optional onMessage callback for debugging
      if (typeof onMessage === 'function') {
        onMessage(event);
      }

      const parsed = parsePayixResponse(event.data);

      if (parsed.kind === PAYIX_RESPONSE_KINDS.SUCCESS) {
        debugPayix('Token response received (success, lastFour=%s)', parsed.token.lastFourDigits);
        if (typeof onToken === 'function') {
          onToken(parsed.token);
        }
      } else if (parsed.kind === PAYIX_RESPONSE_KINDS.FAILURE) {
        debugPayix('Token response received (error): %s', parsed.message);
        const tokenError = new Error(parsed.message);
        reportPayixError(tokenError, { reason: 'token_creation_failed', paymentType });
        if (typeof onError === 'function') {
          onError(tokenError);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeUrl, onToken, onError, onMessage, payixOrigin, paymentType]);

  // Handle iframe load
  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Error state
  if (error) {
    return (
      <div className={className} style={style}>
        <div style={{ padding: '2rem', color: 'red', textAlign: 'center' }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  // No URL state
  if (!iframeUrl) {
    return (
      <div className={className} style={style}>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          Please provide an account number to initialize the payment form.
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ position: 'relative', height, ...style }}>
      {/* Loading overlay */}
      {showLoader && !isLoaded && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          Loading secure payment form...
        </div>
      )}

      {/* Payix iframe */}
      <Iframe
        url={iframeUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        onLoad={handleLoad}
        title="Payix Secure Payment Form"
        style={{ border: 'none', minHeight: '100%', zoom: zoom !== 1 ? zoom : undefined }}
      />
    </div>
  );
}

PayixIframe.propTypes = {
  /** Account or loan number from your system (required) */
  accountNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Callback when token is successfully created */
  onToken: PropTypes.func,
  /** Callback when error occurs */
  onError: PropTypes.func,
  /** Callback for all postMessage events (debugging) */
  onMessage: PropTypes.func,
  /** Payment type: 'card' for credit/debit cards, 'ach' for bank accounts */
  paymentType: PropTypes.oneOf(['card', 'ach', 'both']),
  /** Height of iframe (number in px or string like '450px' or '100%') */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** CSS class name */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
  /** Show loading indicator while iframe loads */
  showLoader: PropTypes.bool,
  /** CSS zoom on the iframe element (e.g. 1.3 = 130%). Uses CSS `zoom` not transform — keeps proper layout semantics. */
  zoom: PropTypes.number,
};
